import React, {useContext, useState} from 'react';
import {
  View,
  Text,
  Linking,
  Platform,
} from 'react-native';

import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import * as Location from 'expo-location';
import * as Notifications from 'expo-notifications';
import * as Sentry from 'sentry-expo';

import {ThemeContext} from '../../../contexts/ThemeContext';
import {UserContext} from '../../../contexts/UserContext';
import StyledButton from '../../../components/StyledButton';
import openLink from '../../../utils/openLink';
import Api from '../../../constants/Api';

const ResponseLink = ({type, url, title, query, handleReprocessQuery, disabled}) => {
  const {t} = useTranslation();
  const {user, userToken, userId, setUser, subscribed} = useContext(UserContext);
  const {navigate} = useNavigation();
  const theme = useContext(ThemeContext);

  const [loading, setLoading] = useState(false);

  switch (type) {
    case 'SIGN_UP':
      return (
        <>
          <Text style={{fontSize: 16, color: theme.textPrimary, marginVertical: 10}}>
            {t(
              'UpgradeMenu.textGuest',
              'You get {{count}} free queries every day as a guest. Create your account to get more.',
              {count: user.free_queries},
            )}
          </Text>

          <StyledButton
            style={{backgroundColor: theme.success}}
            text={t('UpgradeMenu.buttons.createAccount', 'Create my free account')}
            onPress={() => navigate('Signup')}
            disabled={disabled || user.status !== 'guest'}
          />
        </>
      );
    case 'UPGRADE':
      return (
        <>
          <Text style={{fontSize: 16, color: theme.textPrimary, marginVertical: 10}}>
            {t(
              'UpgradeMenu.textUser',
              'You get {{count}} free queries every day. Upgrade to a premium account to get unlimited queries and much more.',
              {count: (user || {}).free_queries},
            )}
          </Text>

          <StyledButton
            style={{backgroundColor: theme.success}}
            text={t('UpgradeMenu.buttons.upgrade', 'Upgrade account')}
            onPress={() => navigate('Upgrade')}
            disabled={disabled || subscribed}
          />
        </>
      );
    case 'PREMIUM_FEATURE':
      return (
        <>
          <Text style={{fontSize: 16, color: theme.textPrimary, marginVertical: 10}}>
            {t(
              'ResponseLinks.premiumFeature.text',
              'You need a premium account to get access to this feature.',
            )}
          </Text>

          <StyledButton
            style={{backgroundColor: theme.success}}
            text={t('UpgradeMenu.buttons.upgrade', 'Upgrade account')}
            onPress={() => navigate('Upgrade')}
            disabled={disabled || subscribed}
          />
        </>
      );
    case 'PERMISSION_LOCATION':
      return (
        <StyledButton
          text={t('ResponseLinks.button.permissionLocation', 'Activate geolocation')}
          loading={loading}
          disabled={disabled}
          onPress={async () => {
            const {canAskAgain} = await Location.getForegroundPermissionsAsync();

            if (canAskAgain) {
              try {
                const {status} = await Location.requestForegroundPermissionsAsync();

                if (status === 'granted') {
                  setLoading(true);
                  const location = await Location.getCurrentPositionAsync({enableHighAccuracy: false});

                  handleReprocessQuery(query, {
                    latitude: location.coords.latitude,
                    longitude: location.coords.longitude,
                  });
                }
              } catch (error) {
                // TODO: Handle error
                if (Platform.OS === 'web') {
                  Sentry.Browser.captureException(error);
                } else {
                  Sentry.Native.captureException(error);
                }
              } finally {
                setLoading(false);
              }
            } else {
              Linking.openSettings();
            }
          }}
        />
      );
    case 'PERMISSION_PUSH_NOTIFICATION':
      return (
        <StyledButton
          disabled={disabled || Platform.OS === 'web'}
          text={t('ResponseLinks.button.permissionPushNotification', 'Activate push notification')}
          loading={loading}
          onPress={async () => {
            const {canAskAgain} = await Notifications.getPermissionsAsync();

            if (canAskAgain) {
              const {status} = await Notifications.requestPermissionsAsync();

              if (status === 'granted') {
                try {
                  setLoading(true);

                  const token = (await Notifications.getExpoPushTokenAsync()).data;

                  const result = await fetch(`${Api.apiBaseUrl}/users/${userId}`, {
                    method: 'PATCH',
                    headers: {
                      'Accept': 'application/json',
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      user_token: userToken,
                      push_token: token,
                      has_notifications_enabled: true,
                    }),
                  });

                  const updatedUser = await result.json();

                  setUser(updatedUser);
                  setLoading(false);

                  // Note: We can't send an empty update but we don't need to update anything on the query, since it's
                  //  the user that has been updated, so we send the same text back.
                  handleReprocessQuery(query, {text: query.text});
                } catch (error) {
                  // TODO: Handle error
                  console.log('error', error);
                  setLoading(false);

                  if (Platform.OS === 'web') {
                    Sentry.Browser.captureException(error);
                  } else {
                    Sentry.Native.captureException(error);
                  }
                }
              }
            } else {
              Linking.openSettings();
            }
          }}
        />
      );
    case 'URL':
      return (
        <StyledButton
          text={title}
          onPress={() => openLink(url)}
        />
      );
  }
};

const ResponseLinks = ({query, response, handleReprocessQuery, disabled}) => {
  const results = [];

  for (let i = 1; i < 5; i++) {
    if (response[`link_${i}_type`]) {
      results.push(
        <ResponseLink
          key={`${response.id}-link-${i}`}
          type={response[`link_${i}_type`]}
          url={response[`link_${i}_url`]}
          title={response[`link_${i}_title`]}
          query={query}
          handleReprocessQuery={handleReprocessQuery}
          disabled={disabled}
        />
      );
    }
  }

  if (!results.length) return null;

  return (
    <View style={{gap: 12, marginVertical: 10}}>
      {results}
    </View>
  );
};

export default ResponseLinks;
